// AuditEventPeek — one audit line's forensic detail, on PeekHost (ADR-036 §2):
// `?peek=audit:<id>` opens it from the trail's row (the row seeds the cache, the
// by-id read confirms it), from a pasted link or after a refresh. Everything the
// row says plus the payload, the device, the address, the correlation — and ONE
// act in its door (`AuditEventActions.tsx`): "See this action's lines", the lines
// that share this line's correlation (an import run, a compound request), which
// narrows the trail to them. Every instant reads in the PERSON's zone (ADR-082 §2 —
// the trail is a person's screen).

import { useTranslation } from 'react-i18next';
import { useAuditEvent } from '../../../hooks/useAuditTrail.js';
import { useDisplayZone } from '../../../hooks/useDisplayZone.js';
import { formatDate } from '../../../lib/format.js';
import { ContextPanel } from '../../../components/patterns/ContextPanel.js';
import { Alert } from '../../../components/ui/Alert.js';
import { Link } from '../../../components/ui/Link.js';
import { Spinner } from '../../../components/ui/Spinner.js';
import { icons } from '../../../components/ui/icons.js';
import { PeekDetail, actorName, noun, subjectFirst, verb } from './auditRows.js';
import { useAuditEventActions } from './AuditEventActions.js';

export function AuditEventPeek({ projectId, eventId, onClose }: { projectId: string; eventId: string; onClose: () => void }) {
  const { t } = useTranslation('audit');
  const display = useDisplayZone({ kind: 'person' });
  const zone = display.zone ?? 'UTC';
  const query = useAuditEvent(projectId, eventId);
  const ev = query.data;
  const acts = useAuditEventActions(projectId, ev);

  if (!ev) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('title')} leading={<icons.history size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}>
        {query.isError ? (
          <Alert variant="danger">{t('error')}</Alert>
        ) : (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}
      </ContextPanel>
    );
  }

  // An import lifecycle event carries its run id — the door to the imports history.
  const jobId = typeof (ev.new_value as Record<string, unknown> | null)?.jobId === 'string' ? String((ev.new_value as Record<string, unknown>).jobId) : null;
  const rows: Array<[string, string | null]> = [
    [t('peek.when'), formatDate(ev.created_at, 'datetime', { timeZone: zone })],
    [t('peek.actor'), `${actorName(ev, t)} · ${t(`actorKind.${ev.actor_kind}`, { defaultValue: ev.actor_kind })}`],
    [t('peek.channel'), t(`channel.${ev.channel}`, { defaultValue: ev.channel })],
    [t('peek.device'), ev.user_agent],
    [t('peek.ip'), ev.ip_address],
    [t('peek.correlation'), ev.correlation_id],
  ];

  return (
    <ContextPanel
      host="peek"
      onClose={onClose}
      leading={<icons.history size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}
      title={subjectFirst(ev) ? `${ev.entity_label} ${verb(ev, t)}` : `${actorName(ev, t)} ${verb(ev, t)}`}
      kind={noun(ev, t) || (ev.entity_type === 'auth' ? t('entity.auth') : t(`entity.${ev.entity_type}`, { defaultValue: ev.entity_type.replace(/_/g, ' ') }))}
      acts={acts}
    >
      <div className="flex flex-col gap-4">
        {ev.entity_label && <div className="text-sm font-medium text-[var(--color-text-primary)]">{ev.entity_label}</div>}
        <PeekDetail ev={ev} t={t} />
        {jobId && (
          // A commit out of the peek: REPLACES the peek's own entry (the open pushed it), as "Open full page" does.
          <Link to={`/p/${projectId}/imports/${encodeURIComponent(jobId)}`} replace className="text-sm font-medium">
            {t('peek.viewImportRun')}
          </Link>
        )}
        <dl className="flex flex-col gap-1.5 border-t border-[var(--color-divider)] pt-3 text-xs">
          {rows
            .filter(([, v]) => Boolean(v))
            .map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <dt className="text-[var(--color-text-secondary)]">{k}</dt>
                <dd className="text-end text-[var(--color-text-primary)]">
                  <bdi>{v}</bdi>
                </dd>
              </div>
            ))}
        </dl>
      </div>
    </ContextPanel>
  );
}
